import React, { useState, useRef, useEffect } from 'react';
import { Camera, Trash2, X, RefreshCw, Settings, Image as ImageIcon, AlertTriangle, CheckCircle, Eye, FileDown, HelpCircle, Layers, Shield, Users } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { CompanyAdminPanel, SuperAdminPanel } from './AdminPanel';
import pkg from '../package.json';

const APP_VERSION = `v${pkg.version}`;

export default function ProductMatcher() {
  const { profile, company, session, signOut } = useAuth();
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  const [items, setItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);

  // 撮影セッション
  const [currentSessionImages, setCurrentSessionImages] = useState([]);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const batchGalleryRef = useRef(null); // multiple専用（DOM属性固定）
  const inputModeRef = useRef('single');

  // バッチ処理
  const [batchImages, setBatchImages] = useState([]); // [{file, base64}]
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // {current, total}
  const [batchResults, setBatchResults] = useState([]); // 完了済みアイテムの結果

  const [modelName, setModelName] = useState("gemini-3.1-flash-lite-preview");
  const [availableModels, setAvailableModels] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpContent, setHelpContent] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [lotNumber, setLotNumber] = useState('');

  // 店舗情報（Supabase DB から選択）
  const [storeName, setStoreName] = useState(() => localStorage.getItem('pm_storeName') || '');
  const [deptName,  setDeptName]  = useState(() => localStorage.getItem('pm_deptName')  || '');

  useEffect(() => { localStorage.setItem('pm_storeName', storeName); }, [storeName]);
  useEffect(() => { localStorage.setItem('pm_deptName',  deptName);  }, [deptName]);

  // 画像圧縮ロジック
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 2048;
          if (width > height) {
            if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
          } else {
            if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.9));
        };
      };
    });
  };

  const openHelp = async () => {
    const file = profile?.role === 'superadmin' ? '/HELP.md'
      : profile?.role === 'admin' ? '/HELP_ADMIN.md'
      : '/HELP_USER.md';
    if (!helpContent[file]) {
      const res = await fetch(file);
      const text = await res.text();
      setHelpContent(prev => ({ ...prev, [file]: text }));
    }
    setShowHelp(true);
  };

  // モデル一覧取得
  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const devApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const response = devApiKey
        ? await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${devApiKey}`)
        : await fetch('/api/models');
      const data = await response.json();
      if (data.error) throw new Error(data.error.message || JSON.stringify(data.error));

      const raw = data.models || [];
      const models = devApiKey
        ? raw.filter(m => m.supportedGenerationMethods?.includes('generateContent')).map(m => m.name.replace('models/', ''))
        : raw;
      setAvailableModels(models);

      const flash = models.find(m => m.includes("flash") && !m.includes("lite"));
      const lite = models.find(m => m.includes("flash-lite"));
      const bestModel = flash || lite || models[0];
      if (bestModel && !models.includes(modelName)) {
        setModelName(bestModel);
      }
      alert(`利用可能なモデルを ${models.length} 件取得しました。`);
    } catch (error) {
      alert("モデル一覧の取得に失敗しました。\n" + error.message);
    } finally {
      setLoadingModels(false);
    }
  };

  // 検査結果をDBに保存（シリアル番号を返すPromise）
  const saveInspection = (item, lotNum) => {
    return fetch('/api/inspect-save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id: company?.id || null,
        user_email: session?.user?.email || null,
        store_name: storeName || null,
        dept_name: deptName || null,
        lot_number: lotNum || null,
        label_product_name: item.label_product_name || null,
        detected_food_visual: item.detected_food_visual || null,
        match_status: item.match_status || null,
        reason: item.reason || null,
        ingredients_check: item.ingredients_check || null,
        model_used: item.modelUsed || null,
        analyze_ms: item.analyzeMs || null,
        images: item.images || [],
      }),
    })
      .then(r => r.json())
      .catch(() => ({}));
  };

  // 画像解析
  const SYSTEM_PROMPT = `あなたはスーパーマーケットの厳格な「品質管理責任者」です。テレコ（ラベルの貼り間違い）・ラベル間不整合・個数ミスを検出してください。\n\n## 思考プロセス（Chain of Thought）\n1. **食品の視覚的解析**: 食品の見た目から食材を特定してください（肉/魚、質感など）。包装や海苔で中身が見えない場合は detected_food_visual を「確認不能」としてください。\n2. **個数の確認**: 全個数が明確に見える場合のみ detected_quantity に記録。袋・重なりで確認不能なら quantity_status を UNCERTAIN に。\n3. **ラベルの読取（全画像分）**: 画像1のラベル → label_product_name。画像2がある場合はそのラベル → label2_product_name（なければ null）。ラベル全体（商品名・パッケージ表記・サブテキスト含む）から個数表記を探し label_quantity に。「2個入り」「3本入」などの〇個入り形式も含む。\n4. **批判的照合**（以下のいずれかで DANGER）: ①label2があり label1と別商品 → DANGER、②中身が見えて食材不一致 → DANGER、③quantity_statusがMISMATCH → DANGER。UNCERTAIN は DANGER にしない。\n\n## 出力形式 (JSONのみ)\n{"detected_food_visual":"...","label_product_name":"...","label2_product_name":"2枚目ラベル商品名またはnull","label_consistency":"MATCH|MISMATCH|N/A","label_quantity":"3切れなどまたはnull","detected_quantity":3,"quantity_status":"MATCH|MISMATCH|UNCERTAIN|N/A","match_status":"SAFE|DANGER","reason":"...","ingredients_check":"..."}`;

  const analyzeImages = async (base64Images) => {
    setAnalyzing(true);
    const startTime = Date.now();
    try {
      const devApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      let data;

      if (devApiKey) {
        const imageParts = base64Images.map(img => ({
          inline_data: {
            mime_type: img.startsWith('data:image/png') ? 'image/png' : 'image/jpeg',
            data: img.split(',')[1]
          }
        }));
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${devApiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: SYSTEM_PROMPT }, ...imageParts] }],
              generationConfig: { response_mime_type: 'application/json' }
            })
          }
        );
        const raw = await response.json();
        if (raw.error) throw new Error(raw.error.message || JSON.stringify(raw.error));
        const text = raw.candidates?.[0]?.content?.parts?.[0]?.text || '';
        data = JSON.parse(text.replace(/```json\n?|\n?```/g, '').trim());
      } else {
        const response = await fetch('/api/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64Images, modelName, companyId: company?.id || null })
        });
        data = await response.json();
        if (data.error) throw new Error(data.error);
      }

      const analyzeMs = Date.now() - startTime;
      setLotNumber('');
      setEditingItem({ id: Date.now(), images: base64Images, lotNumber: '', analyzeMs, modelUsed: modelName, ...data });
      setCurrentSessionImages([]);
      setShowSessionModal(false);

      // 使用量をカウント
      if (company?.id) {
        supabase.rpc('increment_usage', { p_company_id: company.id, p_count: 1 });
      } else if (session?.user?.email) {
        fetch('/api/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: session.user.email, matchCount: 1 }),
        }).catch(() => {});
      }
    } catch (error) {
      alert("解析エラー: " + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleBatchSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) { event.target.value = ''; return; }
    const compressed = await Promise.all(files.map(f => compressImage(f)));
    setBatchImages(prev => [...prev, ...compressed.map((base64, i) => ({ name: files[i].name, base64 }))]);
    if (!showBatchModal) setShowBatchModal(true);
    event.target.value = '';
  };

  const processBatch = async () => {
    if (batchImages.length === 0) return;
    setAnalyzing(true);
    setBatchProgress({ current: 0, total: batchImages.length });
    setBatchResults([]);
    const results = [];
    const savePromises = [];
    for (let i = 0; i < batchImages.length; i++) {
      setBatchProgress({ current: i + 1, total: batchImages.length });
      const { base64 } = batchImages[i];
      try {
        const startTime = Date.now();
        const devApiKey = process.env.REACT_APP_GEMINI_API_KEY;
        let data;
        if (devApiKey) {
          const imagePart = { inline_data: { mime_type: base64.startsWith('data:image/png') ? 'image/png' : 'image/jpeg', data: base64.split(',')[1] } };
          const response = await fetch(
            `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${devApiKey}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contents: [{ parts: [{ text: SYSTEM_PROMPT }, imagePart] }], generationConfig: { response_mime_type: 'application/json' } }) }
          );
          const raw = await response.json();
          if (raw.error) throw new Error(raw.error.message);
          data = JSON.parse(raw.candidates[0].content.parts[0].text.replace(/```json\n?|\n?```/g, '').trim());
        } else {
          const response = await fetch('/api/analyze', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ base64Images: [base64], modelName, companyId: company?.id || null })
          });
          data = await response.json();
          if (data.error) throw new Error(data.error);
        }
        const resultItem = { id: Date.now() + i, images: [base64], lotNumber: '', analyzeMs: Date.now() - startTime, modelUsed: modelName, ...data };
        results.push(resultItem);
        setBatchResults(prev => [...prev, resultItem]);
        savePromises.push(saveInspection(resultItem, ''));
      } catch (err) {
        const errorItem = { id: Date.now() + i, images: [base64], lotNumber: '', match_status: 'ERROR', reason: err.message, label_product_name: batchImages[i].name };
        results.push(errorItem);
        setBatchResults(prev => [...prev, errorItem]);
        savePromises.push(saveInspection(errorItem, ''));
      }
      if (i < batchImages.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    const serialResults = await Promise.allSettled(savePromises);
    const resultsWithSerial = results.map((item, i) => {
      const s = serialResults[i];
      return (s?.status === 'fulfilled' && s.value?.serial) ? { ...item, serial: s.value.serial } : item;
    });
    setItems(prev => [...[...resultsWithSerial].reverse(), ...prev]);
    setShowBatchModal(false);
    setBatchImages([]);
    setBatchProgress(null);
    setBatchResults([]);
    setAnalyzing(false);

    // 使用量をバッチ件数分カウント（エラー分は除く）
    const successCount = results.filter(r => r.match_status !== 'ERROR').length;
    if (successCount > 0) {
      if (company?.id) {
        supabase.rpc('increment_usage', { p_company_id: company.id, p_count: successCount });
      } else if (session?.user?.email) {
        fetch('/api/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: session.user.email, matchCount: successCount }),
        }).catch(() => {});
      }
    }
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);

        if (currentSessionImages.length >= 2) {
          alert("画像は最大2枚までです。");
          event.target.value = '';
          return;
        }

        const newImages = [...currentSessionImages, compressed];
        setCurrentSessionImages(newImages);
        setShowSessionModal(true);
      } catch (err) {
        alert("画像処理エラー");
      }
      event.target.value = '';
    }
  };

  const saveItem = async (item) => {
    const newItem = { ...item, lotNumber };
    setItems(prev => [newItem, ...prev]);
    setEditingItem(null);
    setLotNumber('');
    const data = await saveInspection(item, lotNumber);
    if (data?.serial) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, serial: data.serial } : i));
    }
  };

  const getRiskColor = (status) => {
    return status === 'DANGER'
      ? 'bg-red-100 text-red-800 border-red-200'
      : 'bg-blue-100 text-blue-800 border-blue-200';
  };

  // 別ウィンドウHTMLを生成して印刷（Android対応）
  const exportAndClear = () => {
    if (items.length === 0) return;

    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const fileDate = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    const storePart = storeName ? `_${storeName}` : '';
    const deptPart  = deptName  ? `_${deptName}`  : '';
    const filename  = `検証レポート${storePart}${deptPart}_${fileDate}`;

    const safeCount  = items.filter(i => i.match_status === 'SAFE').length;
    const dangerCount = items.filter(i => i.match_status === 'DANGER').length;
    const tdS = 'padding:4px 8px;border:1px solid #ddd;font-size:10px;vertical-align:top';

    const rowsHtml = items.map((item, idx) => `
      <tr style="background:${idx % 2 === 0 ? '#fff' : '#f9f9f9'}">
        <td style="${tdS}">${idx + 1}</td>
        <td style="${tdS};width:70px"><img src="${item.images[0]}" style="width:60px;height:60px;object-fit:cover;border-radius:4px" /></td>
        <td style="${tdS}">${item.lotNumber || '―'}</td>
        <td style="${tdS}">${item.label_product_name || ''}</td>
        <td style="${tdS}">${item.detected_food_visual || ''}</td>
        <td style="${tdS};font-weight:bold;color:${item.match_status === 'DANGER' ? 'red' : 'green'}">${item.match_status === 'SAFE' ? '✓ SAFE' : '✗ DANGER'}</td>
        <td style="${tdS}">${item.label2_product_name != null ? `[ラベル②: ${item.label2_product_name} → ${item.label_consistency === 'MISMATCH' ? '<span style="color:red;font-weight:bold">ラベルNG</span>' : 'ラベルOK'}] ` : ''}${item.label_quantity != null ? `[個数: ラベル${item.label_quantity} / 実数${item.detected_quantity != null ? item.detected_quantity : '?'}個 → ${item.quantity_status === 'MISMATCH' ? '<span style="color:orange;font-weight:bold">個数NG</span>' : item.quantity_status === 'UNCERTAIN' ? '要目視' : '個数OK'}] ` : ''}${item.reason || ''}${item.ingredients_check ? ' ⚠️ ' + item.ingredients_check : ''}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="ja"><head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${filename}</title>
  <style>
    body{font-family:sans-serif;padding:16px;font-size:11px}
    h2{font-size:16px;margin:0 0 8px}
    p{margin:2px 0}
    table{width:100%;border-collapse:collapse;margin-top:12px}
    th{background:#3f51b5;color:#fff;padding:6px 8px;text-align:left;border:1px solid #ccc;font-size:11px}
    @media print{@page{margin:10mm}}
  </style>
</head><body>
  <h2>ProductMatcher 検証レポート</h2>
  <p>店舗: ${storeName || '―'}　/　部門: ${deptName || '―'}</p>
  <p>出力日時: ${dateStr}</p>
  <p>合計: ${items.length}件　SAFE: ${safeCount}件　<span style="color:red">DANGER: ${dangerCount}件</span></p>
  <table>
    <thead><tr>
      <th>#</th><th>画像</th><th>ロット</th><th>ラベル商品名</th><th>AIの見た中身</th><th>判定</th><th>理由</th>
    </tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setItems([]);
  };

  return (
    <>
    {showAdminPanel === 'admin' && <CompanyAdminPanel onClose={() => setShowAdminPanel(false)} />}
    {showAdminPanel === 'superadmin' && <SuperAdminPanel onClose={() => setShowAdminPanel(false)} />}
    <div className="app-root min-h-screen bg-slate-50 text-slate-800 font-sans pb-32 overflow-x-hidden">
      <header className="bg-indigo-700 text-white p-4 shadow-md sticky top-0 z-10">
        <div className="flex justify-between items-center max-w-md mx-auto gap-2">
          <h1 className="text-xl font-bold flex items-center gap-2 min-w-0 shrink">
            <Eye className="w-6 h-6 shrink-0" /> <span className="truncate">ProductMatcher</span>
            {profile && (
              <span className="text-xs font-normal text-indigo-300 shrink-0">({profile.name || company?.name})</span>
            )}
          </h1>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                onClick={exportAndClear}
                className="text-xs bg-green-600 hover:bg-green-500 px-2 py-1 rounded flex items-center gap-1"
              >
                <FileDown className="w-4 h-4" /> PDF出力 & クリア ({items.length})
              </button>
            )}
            {profile?.role === 'superadmin' && (
              <button onClick={() => setShowAdminPanel('superadmin')} className="text-xs bg-slate-600 px-2 py-1 rounded flex items-center gap-1">
                <Shield className="w-4 h-4" />
              </button>
            )}
            {(profile?.role === 'admin' || profile?.role === 'superadmin') && (
              <button onClick={() => setShowAdminPanel('admin')} className="text-xs bg-indigo-800 px-2 py-1 rounded flex items-center gap-1">
                <Users className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setShowSettings(!showSettings)} className="text-xs bg-indigo-800 px-2 py-1 rounded flex items-center gap-1">
              <Settings className="w-4 h-4" /> 設定
            </button>
            <button onClick={openHelp} className="text-xs bg-indigo-800 px-2 py-1 rounded flex items-center gap-1">
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-4">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg">
              <h2 className="text-lg font-bold text-indigo-700 flex items-center gap-2"><HelpCircle className="w-5 h-5" /> ヘルプ</h2>
              <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 prose prose-sm max-w-none overflow-y-auto" style={{maxHeight: '75vh'}}>
              <ReactMarkdown>{helpContent[profile?.role === 'superadmin' ? '/HELP.md' : profile?.role === 'admin' ? '/HELP_ADMIN.md' : '/HELP_USER.md'] || ''}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-md mx-auto p-4">
        {showSettings && (
          <div className="bg-white p-4 rounded-lg shadow mb-4 border border-indigo-100 space-y-3">
            {/* ログイン情報 */}
            {session && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-slate-500">ログイン中</div>
                  <div className="text-sm text-slate-700">{profile?.name || session.user?.email || '（名前未設定）'}</div>
                  {company && <div className="text-xs text-slate-400">{company.name}</div>}
                </div>
                <button onClick={signOut} className="text-xs text-slate-400 hover:text-red-500">ログアウト</button>
              </div>
            )}

            {/* 店舗情報 */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">店舗名</label>
                <input
                  type="text"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder="例: ○○スーパー"
                  className="w-full p-2 border rounded bg-slate-50 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">部門名</label>
                <input
                  type="text"
                  value={deptName}
                  onChange={(e) => setDeptName(e.target.value)}
                  placeholder="例: 惣菜部"
                  className="w-full p-2 border rounded bg-slate-50 text-sm"
                />
              </div>
            </div>

            {/* モデル選択（SuperAdminのみ） */}
            {profile?.role === 'superadmin' && (
              <div>
                <div className="flex justify-between items-end mb-1">
                  <label className="block text-xs font-bold text-slate-500">使用モデル (Engine)</label>
                  <button
                    onClick={fetchModels}
                    disabled={loadingModels}
                    className="text-xs text-indigo-600 flex items-center gap-1 hover:underline disabled:text-slate-400"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingModels ? 'animate-spin' : ''}`} />
                    一覧取得
                  </button>
                </div>

                {availableModels.length > 0 ? (
                  <select
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    className="w-full p-2 border rounded bg-white text-sm"
                  >
                    {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    placeholder="例: gemini-2.0-flash-lite"
                    className="w-full p-2 border rounded bg-slate-50 text-sm"
                  />
                )}
                <p className="text-xs text-slate-400 mt-1">
                  ※現在: <span className="font-mono font-bold text-slate-600">{modelName}</span> を使用中
                </p>
              </div>
            )}

            {/* バージョン */}
            <div className="text-right">
              <span className="text-xs text-slate-400 font-mono">{APP_VERSION}</span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <h2 className="text-sm font-bold text-slate-500 mb-2">照合履歴</h2>
          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-400 border border-dashed rounded-lg">
              <Eye className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>中身とラベルを撮影して<br/>貼り間違いをチェック</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
                <div className="flex gap-4 mb-2">
                  <div className="w-16 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0 relative">
                    <img src={item.images[0]} className="w-full h-full object-cover" alt="product" />
                    {item.images.length > 1 && (
                      <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] px-1 font-bold">+{item.images.length - 1}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-bold px-2 py-1 rounded inline-block mb-1 ${getRiskColor(item.match_status)}`}>
                      {item.match_status === 'SAFE' ? '一致 (OK)' : '不一致 (NG)'}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.serial && (
                        <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-mono">#{item.serial}</span>
                      )}
                      {item.lotNumber && (
                        <span className="text-xs text-slate-400 font-mono">Lot: {item.lotNumber}</span>
                      )}
                    </div>
                    <div className="text-sm font-bold text-slate-700 truncate">ラ①: {item.label_product_name}</div>
                    {item.label2_product_name != null && (
                      <div className="text-sm text-slate-600 truncate">ラ②: {item.label2_product_name}</div>
                    )}
                    <div className="text-xs text-slate-500 truncate">目: {item.detected_food_visual}</div>
                  </div>
                </div>

                {item.match_status === 'DANGER' && (
                  <div className="bg-red-50 p-2 rounded text-sm text-red-700 mb-2 border border-red-100 font-bold">
                    <AlertTriangle className="w-3 h-3 inline mr-1"/>
                    {item.reason}
                  </div>
                )}

                {item.quantity_status === 'MISMATCH' && item.match_status !== 'DANGER' && (
                  <div className="bg-orange-50 p-2 rounded text-sm text-orange-700 mb-2 border border-orange-100 font-bold">
                    <AlertTriangle className="w-3 h-3 inline mr-1"/>
                    個数不一致: ラベル {item.label_quantity} / 実数 {item.detected_quantity}個
                  </div>
                )}

                {item.quantity_status === 'UNCERTAIN' && (
                  <div className="text-xs text-yellow-600 mb-1">
                    🔢 個数: {item.label_quantity} ／ 目視確認推奨（袋・重なりで自動計数不可）
                  </div>
                )}

                {item.label_quantity != null && item.quantity_status === 'MATCH' && (
                  <div className="text-xs text-slate-400 mb-1">
                    🔢 個数: {item.label_quantity} = {item.detected_quantity}個 ✓
                  </div>
                )}

                <div className="flex justify-between items-center mt-2">
                  {item.analyzeMs && (
                    <span className="text-[10px] text-slate-300 font-mono">⏱ {(item.analyzeMs / 1000).toFixed(2)}s · {item.modelUsed}</span>
                  )}
                  <button
                    onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                    className="text-slate-300 hover:text-red-500 text-xs flex items-center gap-1 ml-auto"
                  >
                    <Trash2 className="w-3 h-3" /> 削除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      <div className="fixed bottom-6 left-0 right-0 flex justify-center items-center gap-6 z-20">
        <button onClick={() => galleryInputRef.current.click()} disabled={analyzing} className="w-14 h-14 rounded-full shadow-lg bg-white text-indigo-600 flex items-center justify-center">
          <ImageIcon className="w-6 h-6" />
        </button>
        <button onClick={() => cameraInputRef.current.click()} disabled={analyzing} className="rounded-full shadow-xl bg-indigo-600 text-white flex items-center justify-center" style={{width:'4.5rem', height:'4.5rem'}}>
          <Camera className="w-8 h-8" />
        </button>
        <button onClick={() => { setBatchImages([]); batchGalleryRef.current.value = ''; batchGalleryRef.current.click(); }} disabled={analyzing} title="バッチ処理" className="w-14 h-14 rounded-full shadow-lg bg-white text-indigo-600 flex items-center justify-center">
          <Layers className="w-6 h-6" />
        </button>
        <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} className="hidden"
          onChange={(e) => { const isBatch = inputModeRef.current === 'batch'; inputModeRef.current = 'single'; isBatch ? handleBatchSelect(e) : handleImageSelect(e); }} />
        <input type="file" accept="image/*" ref={galleryInputRef} className="hidden" onChange={handleImageSelect} />
        <input type="file" accept="image/*" multiple ref={batchGalleryRef} className="hidden" onChange={handleBatchSelect} />
      </div>

      {/* 撮影セッションモーダル */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/80 z-30 flex flex-col justify-end pb-8">
          <div className="p-4 text-white text-center">
            <h3 className="text-lg font-bold mb-2">撮影画像を確認 ({currentSessionImages.length}/2)</h3>
            <p className="text-sm opacity-80 mb-4">中身とラベルを別撮りする場合は追加できます</p>

            <div className="flex gap-2 justify-center mb-6 overflow-x-auto px-4">
              {currentSessionImages.map((img, idx) => (
                <div key={idx} className="w-20 h-20 rounded border-2 border-white overflow-hidden flex-shrink-0 relative">
                  <img src={img} className="w-full h-full object-cover" alt={`shot-${idx}`} />
                  <button
                    onClick={() => setCurrentSessionImages(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {currentSessionImages.length < 2 && (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => cameraInputRef.current.click()}
                    className="w-20 h-9 rounded border border-white/50 flex items-center justify-center text-white bg-indigo-600 hover:bg-indigo-500"
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    <span className="text-[10px] font-bold">撮影</span>
                  </button>
                  <button
                    onClick={() => galleryInputRef.current.click()}
                    className="w-20 h-9 rounded border border-white/50 flex items-center justify-center text-indigo-600 bg-white hover:bg-slate-100"
                  >
                    <ImageIcon className="w-4 h-4 mr-1" />
                    <span className="text-[10px] font-bold">選択</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => analyzeImages(currentSessionImages)}
              disabled={analyzing || currentSessionImages.length === 0}
              className="w-full max-w-xs mx-auto py-3 bg-indigo-500 hover:bg-indigo-400 text-white font-bold rounded-full shadow-lg flex items-center justify-center gap-2"
            >
              {analyzing ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  解析中...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  まとめて解析する
                </>
              )}
            </button>

            <button onClick={() => { setShowSessionModal(false); setCurrentSessionImages([]); }} className="mt-4 text-sm text-white/60 underline">
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* バッチ処理モーダル */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/80 z-30 flex flex-col justify-end pb-8">
          <div className="p-4 text-white text-center">
            <h3 className="text-lg font-bold mb-1">バッチ解析 ({batchImages.length}件)</h3>
            <p className="text-sm opacity-70 mb-4">各画像を1商品として順番に解析します</p>

            <div className="flex gap-2 justify-start overflow-x-auto px-2 mb-6 pb-2">
              {batchImages.map((img, idx) => (
                <div key={idx} className="flex-shrink-0 relative">
                  <img src={img.base64} className="w-16 h-16 rounded object-cover border-2 border-white/40" alt={`batch-${idx}`} />
                  {batchProgress && batchProgress.current > idx && (
                    <div className={`absolute inset-0 rounded flex items-center justify-center ${batchResults[idx]?.match_status === 'DANGER' ? 'bg-red-500/70' : 'bg-green-500/70'}`}>
                      {batchResults[idx]?.match_status === 'DANGER'
                        ? <AlertTriangle className="w-6 h-6 text-white" />
                        : <CheckCircle className="w-6 h-6 text-white" />}
                    </div>
                  )}
                  {batchProgress && batchProgress.current === idx + 1 && (
                    <div className="absolute inset-0 bg-indigo-500/70 rounded flex items-center justify-center">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    </div>
                  )}
                  {!batchProgress && (
                    <button onClick={() => setBatchImages(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {batchProgress ? (
              <div className="text-center">
                <p className="text-lg font-bold mb-2">{batchProgress.current} / {batchProgress.total} 解析中...</p>
                <div className="w-full max-w-xs mx-auto bg-white/20 rounded-full h-2">
                  <div className="bg-indigo-400 h-2 rounded-full transition-all" style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }} />
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2 justify-center mb-3">
                  <button onClick={() => { inputModeRef.current = 'batch'; cameraInputRef.current.click(); }}
                    className="flex items-center gap-1 px-3 py-2 bg-white/20 text-white rounded-full text-sm font-bold">
                    <Camera className="w-4 h-4" /> 撮影で追加
                  </button>
                </div>
                <button onClick={processBatch} disabled={batchImages.length === 0}
                  className="w-full max-w-xs mx-auto py-3 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white font-bold rounded-full shadow-lg flex items-center justify-center gap-2">
                  <Layers className="w-5 h-5" />
                  {batchImages.length}件をまとめて解析
                </button>
                <button onClick={() => { setShowBatchModal(false); setBatchImages([]); }} className="mt-4 text-sm text-white/60 underline">
                  キャンセル
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editingItem && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className={`p-4 border-b flex justify-between items-center ${getRiskColor(editingItem.match_status)}`}>
              <h3 className="font-bold flex items-center gap-2">
                {editingItem.match_status === 'SAFE' ? <CheckCircle className="w-5 h-5"/> : <AlertTriangle className="w-5 h-5"/>}
                判定: {editingItem.match_status}
              </h3>
              <button onClick={() => setEditingItem(null)}><X className="w-6 h-6" /></button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {editingItem.images.map((img, i) => (
                  <img key={i} src={img} className="h-40 rounded object-contain bg-slate-100 border" alt="preview" />
                ))}
              </div>
              {editingItem.analyzeMs && (
                <div className="flex gap-2 text-xs text-slate-400 font-mono">
                  <span>⏱ {(editingItem.analyzeMs / 1000).toFixed(2)}s</span>
                  <span className="text-slate-300">|</span>
                  <span className="truncate">{editingItem.modelUsed}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded">
                <div>
                  <label className="block text-xs font-bold text-slate-500">📸 AIが見た中身</label>
                  <p className="font-bold text-indigo-800">{editingItem.detected_food_visual}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500">🏷️ ラベル①</label>
                  <p className="font-bold text-slate-800">{editingItem.label_product_name}</p>
                  {editingItem.label2_product_name != null && (
                    <>
                      <label className="block text-xs font-bold text-slate-500 mt-1">🏷️ ラベル②</label>
                      <p className="font-bold text-slate-800">{editingItem.label2_product_name}</p>
                    </>
                  )}
                </div>
              </div>

              {editingItem.label2_product_name != null && (
                <div className={`p-3 rounded border ${editingItem.label_consistency === 'MISMATCH' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <label className="block text-xs font-bold text-slate-500 mb-1">🏷️🏷️ ラベル間チェック</label>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-slate-600">①<strong>{editingItem.label_product_name}</strong></span>
                    <span className="text-slate-400">vs</span>
                    <span className="text-slate-600">②<strong>{editingItem.label2_product_name}</strong></span>
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${editingItem.label_consistency === 'MISMATCH' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
                      {editingItem.label_consistency === 'MISMATCH' ? 'ラベルNG' : 'ラベルOK'}
                    </span>
                  </div>
                </div>
              )}

              {editingItem.label_quantity != null && (
                <div className={`p-3 rounded border ${
                  editingItem.quantity_status === 'MISMATCH' ? 'bg-orange-50 border-orange-200' :
                  editingItem.quantity_status === 'UNCERTAIN' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-green-50 border-green-200'
                }`}>
                  <label className="block text-xs font-bold text-slate-500 mb-1">🔢 個数チェック</label>
                  <div className="flex items-center gap-3 text-sm">
                    <span>ラベル: <strong>{editingItem.label_quantity}</strong></span>
                    <span className="text-slate-400">→</span>
                    <span>実数: <strong>{editingItem.detected_quantity != null ? `${editingItem.detected_quantity}個` : '確認不能'}</strong></span>
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${
                      editingItem.quantity_status === 'MISMATCH' ? 'bg-orange-200 text-orange-800' :
                      editingItem.quantity_status === 'UNCERTAIN' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-green-200 text-green-800'
                    }`}>
                      {editingItem.quantity_status === 'MISMATCH' ? '個数NG' :
                       editingItem.quantity_status === 'UNCERTAIN' ? '要目視確認' : '個数OK'}
                    </span>
                  </div>
                </div>
              )}

              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <label className="block text-xs font-bold text-slate-500 mb-1">AIコメント</label>
                <p className="text-sm font-bold text-slate-800">{editingItem.reason}</p>
                {editingItem.ingredients_check && (
                  <p className="text-sm text-red-600 mt-1">⚠️ {editingItem.ingredients_check}</p>
                )}
              </div>

              {/* ロット番号入力 */}
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">ロット番号・商品番号（任意）</label>
                <input
                  type="text"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  placeholder="例: L2026-001"
                  className="w-full p-2 border rounded bg-slate-50 text-sm"
                />
              </div>
            </div>

            <div className="p-4 border-t">
              <button onClick={() => saveItem(editingItem)} className="w-full py-3 bg-indigo-600 text-white font-bold rounded shadow">OK (確認)</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
